import { useEffect, useState } from 'react';
import {
  Container,
  Butons,
  Context,
  Btt02,
  BottomButons,
  UpperButons,
  ButtonGroup
} from './style';

import AttendanceList from '../../components/attendanceList/index';
import Classes from '../../components/ClassesList/index';
import IndividualFormList from '../../components/IndividualFormList/index';
import BimonthlyAssessments from '../../components/BimonthlyAssessments/index';
import NumericalGradeList from '../../components/NumericalGradeList';
import GradeList from '../../components/GradeList/index';
import FinalGradeList from '../../components/FinalGradeList/index';

// Impressão do diário pelo navegador (botão direito → Imprimir / Ctrl+P).
// Não é a impressão oficial (essa fica no Diário da Turma), mas sai no mesmo padrão:
// A4 paisagem, só o conteúdo do diário, tabelas com borda e as cores de frequência e notas.
const CSS_IMPRESSAO_DIARIO = `
/* usado só por um instante antes de imprimir, para medir a largura real das tabelas */
#daily-print-area.daily-medindo table { width: auto !important; border-collapse: collapse !important; }
#daily-print-area.daily-medindo th, #daily-print-area.daily-medindo td {
  font-size: 10.5px !important; padding: 1px 3px !important; white-space: normal !important;
}
#daily-print-area.daily-medindo .name-cell { white-space: nowrap !important; }

@media print {
  @page { size: A4 landscape; margin: 8mm; }
  html, body { background: #fff !important; height: auto !important; overflow: visible !important; }

  /* só o caminho até o conteúdo do diário fica na página (menu, cabeçalho e abas somem) */
  [data-print-chain] > *:not([data-print-chain]):not([data-print-area]) { display: none !important; }
  [data-print-chain] {
    display: block !important; position: static !important; width: 100% !important;
    min-height: 0 !important; height: auto !important; max-height: none !important;
    margin: 0 !important; padding: 0 !important; overflow: visible !important;
    box-shadow: none !important; background: #fff !important; transform: none !important;
  }

  #daily-print-area { width: 100% !important; min-height: 0 !important; font-family: Arial, sans-serif !important; color: #000 !important; }
  #daily-print-area * {
    box-shadow: none !important; overflow: visible !important; max-height: none !important;
    -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important;
  }
  #daily-print-area .no-print, #daily-print-area button, #daily-print-area input,
  #daily-print-area select, #daily-print-area textarea, #daily-print-area svg { display: none !important; }

  /* cabeçalho (período, escola, professor, série, turma) */
  #daily-print-area h2 { font-size: 16px !important; margin: 0 0 4px !important; color: #000 !important; }
  #daily-print-area h3 { font-size: 13px !important; margin: 6px 0 2px !important; color: #000 !important; }
  #daily-print-area .data, #daily-print-area .info {
    display: flex !important; flex-direction: row !important; flex-wrap: wrap !important;
    gap: 2px 16px !important; margin: 0 0 6px !important; font-size: 12px !important;
  }
  #daily-print-area .info span, #daily-print-area .data span { font-size: 12px !important; color: #000 !important; }

  /* tabelas: igual ao diário oficial */
  #daily-print-area table { width: 100% !important; max-width: 100% !important; border-collapse: collapse !important; margin: 0 !important; }
  #daily-print-area thead { display: table-header-group !important; }
  #daily-print-area tr { page-break-inside: avoid !important; break-inside: avoid !important; }
  #daily-print-area th, #daily-print-area td {
    border: 1px solid #8c8c8c !important; font-size: 10.5px !important; padding: 1px 3px !important;
    text-align: center !important; background: #fff !important; position: static !important;
  }
  #daily-print-area th { font-weight: bold !important; color: #000 !important; }
  #daily-print-area .name-cell { text-align: left !important; white-space: nowrap !important; }
  #daily-print-area td:has(.description), #daily-print-area td .description { text-align: left !important; }
  /* meses alternados na frequência (igual ao diário da turma) */
  #daily-print-area th.month-alt, #daily-print-area td.month-alt { background: #d0d0d0 !important; }
  #daily-print-area th.month-normal, #daily-print-area td.month-normal { background: #fff !important; }
  #daily-print-area .month-alt, #daily-print-area .month-normal { border-right: 1px solid #666 !important; }
  #daily-print-area th.date-cell { padding: 1px !important; }
  #daily-print-area td.presence { color: green !important; font-weight: bold !important; }
  #daily-print-area td.absence { color: red !important; font-weight: bold !important; }
  #daily-print-area td.justifiedAbsence { color: #6a0dad !important; font-weight: bold !important; }
  #daily-print-area .total-presence { color: green !important; }
  #daily-print-area .total-absence { color: red !important; }
  #daily-print-area .total-justifiedAbsence { color: #6a0dad !important; }

  /* aulas, relatórios e avaliações: texto completo, sem cortar entre páginas */
  #daily-print-area .description, #daily-print-area .description * { -webkit-line-clamp: unset !important; white-space: normal !important; }
  #daily-print-area p, #daily-print-area li { font-size: 12px !important; }
  #daily-print-area [class*="Activity"], #daily-print-area [class*="ListChecked"] { break-inside: avoid; page-break-inside: avoid; }
}
`;

// largura útil de uma A4 paisagem com margem de 8 mm (~281 mm ≈ 1060 px)
const LARGURA_A4_PAISAGEM = 1060;

// marca o caminho do <body> até o conteúdo, para a impressão esconder todo o resto,
// e reduz (zoom) só as tabelas que não caberiam na largura da folha — nada é cortado
const marcarCaminhoDeImpressao = () => {
  const area = document.getElementById('daily-print-area');
  if (!area) return;
  area.setAttribute('data-print-area', '');
  area.classList.add('daily-medindo');
  const larguras = [];
  area.querySelectorAll('table').forEach((t) => {
    if (t.parentElement && t.parentElement.closest('table')) return; // tabela dentro de tabela: a de fora resolve
    larguras.push([t, t.scrollWidth]);
  });
  area.classList.remove('daily-medindo');
  larguras.forEach(([t, largura]) => {
    if (largura > LARGURA_A4_PAISAGEM) {
      t.setAttribute('data-print-zoom', t.style.zoom || '');
      t.style.zoom = String(Math.max(0.4, LARGURA_A4_PAISAGEM / largura));
    }
  });
  let el = area.parentElement;
  while (el && el !== document.documentElement) {
    el.setAttribute('data-print-chain', '');
    el = el.parentElement;
  }
};
const desmarcarCaminhoDeImpressao = () => {
  document.querySelectorAll('[data-print-zoom]').forEach((t) => {
    t.style.zoom = t.getAttribute('data-print-zoom') || '';
    t.removeAttribute('data-print-zoom');
  });
  document.querySelectorAll('[data-print-chain]').forEach((el) => el.removeAttribute('data-print-chain'));
  document.querySelectorAll('[data-print-area]').forEach((el) => el.removeAttribute('data-print-area'));
};
const Daily = () => {
  const [activeComponent, setActiveComponent] = useState(() => {
    return sessionStorage.getItem('activeComponent') || 'attendanceList';
  });
  const [assessmentFormat, setassessmentFormat] = useState('');

  useEffect(() => {
    (async () => {
      const $assessmentFormat = sessionStorage.getItem('assessmentFormat')
      setassessmentFormat($assessmentFormat)
    })();
  }, []);

  // impressão pelo navegador: prepara a página antes e limpa depois
  useEffect(() => {
    window.addEventListener('beforeprint', marcarCaminhoDeImpressao);
    window.addEventListener('afterprint', desmarcarCaminhoDeImpressao);
    return () => {
      window.removeEventListener('beforeprint', marcarCaminhoDeImpressao);
      window.removeEventListener('afterprint', desmarcarCaminhoDeImpressao);
      desmarcarCaminhoDeImpressao();
    };
  }, []);

  const handleComponentChange = (component) => {
    if (activeComponent !== component) {
      sessionStorage.setItem('activeComponent', component);
      setActiveComponent(component); // Agora apenas atualiza o estado
    }
  };

  const isActive = (component) => activeComponent === component;

  const renderActiveComponent = () => {
    switch (activeComponent) {
      case 'attendanceList':
        return <AttendanceList />;
      case 'classes':
        return <Classes />;
      case 'individualRecords':
        return <IndividualFormList />;
      case 'concepts':
        return <GradeList />;
      case 'assessments':
        return <BimonthlyAssessments />
      case 'numericalGrades':
        return <NumericalGradeList />;
      case 'finalConcepts':
        return <FinalGradeList />;
      default:
        return null;
    }
  };

  return (
    <Container>
      <style>{CSS_IMPRESSAO_DIARIO}</style>
      <Butons className="no-print">

        <UpperButons>
          <Btt02
            className={isActive('attendanceList') ? 'active' : ''}
            onClick={() => handleComponentChange('attendanceList')}
          >
            Frequência
          </Btt02>
          <Btt02 className={isActive('classes') ? 'active' : ''}
            onClick={() => handleComponentChange('classes')}
          >
            Aulas Lecionadas
          </Btt02>
          {assessmentFormat !== 'grade' ? (
            <Btt02
              className={isActive('concepts') ? 'active' : ''}
              onClick={() => handleComponentChange('concepts')}
            >
              Conceitos
            </Btt02>
          ) : (
            <ButtonGroup>
              <Btt02 className={isActive('assessments') ? 'active' : ''}
                onClick={() => handleComponentChange('assessments')}
              >
                Avaliações
              </Btt02>
              <Btt02
                className={isActive('numericalGrades') ? 'active' : ''}
                onClick={() => handleComponentChange('numericalGrades')}
              >
                Notas
              </Btt02>
            </ButtonGroup>
          )}

        </UpperButons>

        {assessmentFormat !== "grade"
          &&
          <BottomButons>
            <Btt02
              className={isActive('individualRecords') ? 'active' : ''}
              onClick={() => handleComponentChange('individualRecords')}
            >
              Relatórios Individuais
            </Btt02>
            <Btt02
              className={isActive('finalConcepts') ? 'active' : ''}
              onClick={() => handleComponentChange('finalConcepts')}
            >
              Conceitos Finais
            </Btt02>
          </BottomButons>
        }

      </Butons>
      <Context id="daily-print-area">
        {renderActiveComponent()} {/* Renderiza o componente ativo */}
      </Context>
    </Container>
  );
};

export default Daily;
